import React, { Suspense, lazy, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DialogProvider, ToastProvider } from '@thefarmersfront/kitchen-mobile';

import AuthRoute from './components/AuthRoute/AuthRoute';
import DevToolsFab from './components/BackDoor/DevToolsFab';
import ChangePasswordModal from './components/ChangePasswordModal/ChangePasswordModal';
import Layout from './components/Layout/Layout';
import LoadingIndicator from './components/LoadingIndicator/LoadingIndicator';
import { BrowserOnly, CatchAllGuard, RootGuard, WebViewOnly } from './components/RouteGuards';
import Entry from './pages/Entry/Entry';
import { GlobalErrorFallback, GlobalRouteErrorFallback } from './pages/Fallback/GlobalErrorFallback';
import GlobalSuspenseFallback from './pages/Fallback/GlobalSuspenseFallback';

import { useGetAppTypeByApp } from './hooks';
import { BottomSheetProvider } from './services/bottomSheetService';
import { setGlobalNavigate } from './services/navigateService';

const NotFound = lazy(() => import('./pages/Fallback/NotFound'));
const Login = lazy(() => import('./pages/Login/Login'));
const CheckNetwork = lazy(() => import('./pages/CheckNetwork/CheckNetwork'));
const BeforeWork = lazy(() => import('./pages/BeforeWork/BeforeWork'));
const ContractCaution = lazy(() => import('./pages/Contract/Caution'));
const ContractUserInfo = lazy(() => import('./pages/Contract/ContractUserInfo'));
const SearchPostCode = lazy(() => import('./pages/SearchPostCode/SearchPostCode'));
const ContractWorkInfo = lazy(() => import('./pages/Contract/ContractWorkInfo'));
const ContractPhoneCertificate = lazy(() => import('./pages/Contract/PhoneCertificate'));
const ContractSign = lazy(() => import('./pages/Contract/ContractSign'));
const Register = lazy(() => import('./pages/Register/Register'));
const RegisterPhoneVerification = lazy(() => import('./pages/Register/PhoneVerification'));
const FindId = lazy(() => import('./pages/FindId/FindId'));
const FindIdSuccess = lazy(() => import('./pages/FindId/FindIdSuccess'));
const ResetPw = lazy(() => import('./pages/ResetPw/ResetPw'));
const SetNewPw = lazy(() => import('./pages/ResetPw/SetNewPw'));
const StartWork = lazy(() => import('./pages/StartWork/StartWork'));
const SelectWorkInfo = lazy(() => import('./pages/StartWork/SelectWorkInfo'));
const MonthlyTimeline = lazy(() => import('./pages/MonthlyTimeline/MonthlyTimeline'));
const RegisterWorkPlan = lazy(() => import('./pages/MonthlyTimeline/RegisterWorkPlan'));
const LeaveEarly = lazy(() => import('./pages/LeaveEarly/LeaveEarly'));
const SettingsCheckPassword = lazy(() => import('./pages/Settings/SettingsCheckPassword'));
const SubmitSpecialMedical = lazy(() => import('./pages/SubmitSpecialMedical/SubmitSpecialMedical'));
const SubmitSpecialMedicalPreviewImage = lazy(() => import('./pages/SubmitSpecialMedical/SpecialMedicalImagePreview'));
const SubmitSpecialMedicalCameraResult = lazy(() => import('./pages/SubmitSpecialMedical/CameraResult'));
const ContractManagement = lazy(() => import('./pages/ContractManagement/ContractManagement'));
const PayStatement = lazy(() => import('./pages/PayStatement/PayStatement'));
const PayStatementCheckPassword = lazy(() => import('./pages/PayStatement/PayStatementCheckPassword'));
const PayStatementDetail = lazy(() => import('./pages/PayStatement/PayStatementDetail'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const ModifyUserInfo = lazy(() => import('./pages/Settings/ModifyUserInfo'));
const ChangePhoneNumber = lazy(() => import('./pages/Settings/ChangePhoneNumber'));
const WithdrawAccount = lazy(() => import('./pages/Settings/WithdrawAccount'));
const ChangePassword = lazy(() => import('./pages/Settings/ChangePassword'));
const RecruitAgreement = lazy(() => import('./pages/Settings/RecruitAgreement'));
const WorkApply = lazy(() => import('./pages/WorkApply/WorkApply'));
const WorkApplyList = lazy(() => import('./pages/WorkApply/WorkApplyList'));
const WorkApplyDetail = lazy(() => import('./pages/WorkApply/WorkApplyDetail'));

const App = () => {
  useGetAppTypeByApp();

  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <>
            {/* Layout이 적용되지 않은 페이지 */}
            <Route element={<WebViewOnly />}>
              <Route
                path="/login"
                element={
                  <Suspense fallback={<GlobalSuspenseFallback />}>
                    <Login />
                  </Suspense>
                }
              />
            </Route>
            {/* Layout이 적용된 페이지 */}
            <Route
              element={
                <Layout>
                  <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
                    <Suspense fallback={<GlobalSuspenseFallback />}>
                      <Outlet />
                    </Suspense>
                    <ChangePasswordModal />
                  </ErrorBoundary>
                </Layout>
              }
              errorElement={<GlobalRouteErrorFallback />}
            >
              {/* 경로가 겹치는 `/`, `*`는 단일 라우트에서 Guard가 직접 분기한다 (무한 리다이렉트 방지) */}
              <Route path="/" element={<RootGuard />} />
              <Route path="*" element={<CatchAllGuard />} />

              {/* 웹뷰 전용 라우트 그룹 */}
              <Route element={<WebViewOnly />}>
                <Route path="/not-found" element={<NotFound />} />
                <Route path="/main" element={<Entry />} />
                <Route path="/work-apply-list" element={<AuthRoute component={<WorkApplyList />} />} />
                <Route path="/work-apply-list/detail/:id" element={<AuthRoute component={<WorkApplyDetail />} />} />
                <Route path="/work-apply/*" element={<AuthRoute component={<WorkApply />} />} />
                <Route path="/before-work" element={<AuthRoute component={<BeforeWork />} />} />
                <Route path="/check-network" element={<AuthRoute component={<CheckNetwork />} />} />
                <Route path="/contract/caution" element={<AuthRoute component={<ContractCaution />} />} />
                <Route path="/contract/contract-user-info" element={<AuthRoute component={<ContractUserInfo />} />} />
                <Route path="/contract/search-post-code" element={<AuthRoute component={<SearchPostCode />} />} />
                <Route path="/contract/contract-work-info" element={<AuthRoute component={<ContractWorkInfo />} />} />
                <Route
                  path="/contract/phone-certificate"
                  element={<AuthRoute component={<ContractPhoneCertificate />} />}
                />
                <Route path="/contract/contract-sign" element={<AuthRoute component={<ContractSign />} />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register/phone-verification" element={<RegisterPhoneVerification />} />
                <Route path="/find-id" element={<FindId />} />
                <Route path="/find-id/success" element={<FindIdSuccess />} />
                <Route path="/reset-pw" element={<ResetPw />} />
                <Route path="/reset-pw/set-new-pw" element={<SetNewPw />} />
                <Route path="/change-pw" element={<SetNewPw />} />
                <Route path="/start-work" element={<AuthRoute component={<StartWork />} />} />
                <Route path="/start-work/select-work-info" element={<AuthRoute component={<SelectWorkInfo />} />} />
                <Route path="/monthly-timeline" element={<AuthRoute component={<MonthlyTimeline />} />} />
                <Route
                  path="/monthly-timeline/register-work-plan"
                  element={<AuthRoute component={<RegisterWorkPlan />} />}
                />
                <Route path="/leave-early" element={<AuthRoute component={<LeaveEarly />} />} />
                <Route path="/submit-special-medical" element={<AuthRoute component={<SubmitSpecialMedical />} />} />
                <Route
                  path="/submit-special-medical/preview-image"
                  element={<AuthRoute component={<SubmitSpecialMedicalPreviewImage />} />}
                />
                <Route
                  path="/submit-special-medical/camera-result"
                  element={<AuthRoute component={<SubmitSpecialMedicalCameraResult />} />}
                />
                <Route path="/contract-management" element={<AuthRoute component={<ContractManagement />} />} />
                <Route path="/pay-statement" element={<AuthRoute component={<PayStatement />} />} />
                <Route
                  path="/pay-statement/check-password"
                  element={<AuthRoute component={<PayStatementCheckPassword />} />}
                />
                <Route path="/pay-statement/detail/:type" element={<AuthRoute component={<PayStatementDetail />} />} />
                <Route path="/settings" element={<AuthRoute component={<Settings />} />} />
                <Route
                  path="/settings/modify-user-info/check-password"
                  element={<AuthRoute component={<SettingsCheckPassword />} />}
                />
                <Route path="/settings/modify-user-info" element={<AuthRoute component={<ModifyUserInfo />} />} />
                <Route path="/settings/search-post-code" element={<AuthRoute component={<SearchPostCode />} />} />
                <Route path="/settings/change-phone-number" element={<AuthRoute component={<ChangePhoneNumber />} />} />
                <Route path="/settings/withdraw-account" element={<AuthRoute component={<WithdrawAccount />} />} />
                <Route path="/settings/change-password" element={<AuthRoute component={<ChangePassword />} />} />
                <Route path="/settings/recruit-agreement" element={<AuthRoute component={<RecruitAgreement />} />} />
              </Route>

              {/* 웹 브라우저 라우트 그룹 */}
              <Route element={<BrowserOnly />}>
                <Route path="/guest">
                  <Route index element={<Navigate to="/" replace />} />
                  <Route path="work-apply-list" element={<WorkApplyList />} />
                  <Route path="work-apply-list/detail/:id" element={<WorkApplyDetail />} />
                  <Route path="work-apply/*" element={<WorkApply />} />
                </Route>
              </Route>
            </Route>
          </>
        )
      ),
    []
  );

  setGlobalNavigate(router.navigate);

  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <LoadingIndicator />
      <DialogProvider />
      <div className="toast-root">
        <ToastProvider duration={1500} />
      </div>
      <BottomSheetProvider />
      <DevToolsFab />
    </QueryClientProvider>
  );
};

export default App;
